import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Typography, IconButton } from '@mui/material';
import { linkProps } from '../../../utils/appLink';
import {
  FOOTER_CATEGORY_LINK_GROUPS,
  CATEGORY_LANDING_HREFS,
} from '../../../utils/productNavigation';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Image from '../../Image/Image';

const HIPPO_FOOTER =
  'https://assets.askhippo.co.za/Pages/Footer/HippoFooter.svg';

const SOCIAL_ICONS = {
  facebook: 'https://assets.askhippo.co.za/Pages/Footer/Facebook.svg',
  linkedin: 'https://assets.askhippo.co.za/Pages/Footer/LinkedIn.svg',
  x: 'https://assets.askhippo.co.za/Pages/Footer/X.png',
  tiktok: 'https://assets.askhippo.co.za/Pages/Footer/TikTok.svg',
  youtube: 'https://assets.askhippo.co.za/Pages/Footer/YouTube.svg',
  instagram: 'https://assets.askhippo.co.za/Pages/Footer/Instagram.svg',
};
const socialLinks = [
  {
    icon: SOCIAL_ICONS.facebook,
    url: 'https://www.facebook.com/HippoSA',
    alt: 'Hippo on Facebook',
    title: 'Follow Hippo on Facebook',
  },
  {
    icon: SOCIAL_ICONS.linkedin,
    url: 'https://www.linkedin.com/company/hippo-comparative-services-pty-ltd/',
    alt: 'Hippo on LinkedIn',
    title: 'Follow Hippo on LinkedIn',
  },
  {
    icon: SOCIAL_ICONS.x,
    url: 'https://twitter.com/hippo-co-za',
    alt: 'Hippo on X (Twitter)',
    title: 'Follow Hippo on X (Twitter)',
  },
  {
    icon: SOCIAL_ICONS.tiktok,
    url: 'https://www.tiktok.com/@hippo_co_za?',
    alt: 'Hippo on TikTok',
    title: 'Follow Hippo on TikTok',
  },
  {
    icon: SOCIAL_ICONS.youtube,
    url: 'https://www.youtube.com/@hippo_co_za?',
    alt: 'Hippo on YouTube',
    title: 'Subscribe to Hippo on YouTube',
  },
  {
    icon: SOCIAL_ICONS.instagram,
    url: 'https://www.instagram.com/hippo.co.za',
    alt: 'Hippo on Instagram',
    title: 'Follow Hippo on Instagram',
  },
];

const navGroups = [
  {
    title: 'Company',
    links: [
      { label: 'About', href: '/about-us/' },
      { label: 'Blog', href: '/blog/' },
      {
        label: 'Contact Us',
        href: '/contact-us/',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
      { label: 'Site Map', href: '/sitemap/' },
    ],
  },
  {
    title: 'Policies',
    links: [
      {
        label: 'Terms & Conditions',
        href: '/legal/terms-and-conditions/',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
      {
        label: 'Privacy Policy',
        href: '/more/privacypolicy2024.pdf',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
      {
        label: 'PAIA Manual',
        href: '/more/PAIA-manual-2026.pdf',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
      { label: 'Marketing', href: '/newsletter-updates/' },
      {
        label: 'Conflict of Interest',
        href: '/more/Conflict_of_Interest_Management_Policy_2024.pdf',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    ],
  },
  ...FOOTER_CATEGORY_LINK_GROUPS,
];

const MEDICAL_AID_DISCLAIMER =
  '*This Medical Aid comparison is powered by Hippo Advisory Services (Pty) Ltd FSP No. 36088. Please note that this service is not available to intermediaries. This article is for informational purposes only and should not be construed as financial, legal or medical advice.';

const KAUFMAN_DISCLAIMER =
  '*Based on independent research by Kaufman Levin & Associates 2025.';

const FooterMobileComponent = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);
  const { pathname } = useLocation();
  const isMedicalAid = pathname.includes('medical-aid-quote');
  const disclaimer = isMedicalAid ? MEDICAL_AID_DISCLAIMER : KAUFMAN_DISCLAIMER;

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: '#FFFFFF',
        p: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
      }}
    >
      {/* Logo */}
      <Image
        src={HIPPO_FOOTER}
        alt="Hippo comparison service logo"
        title="Hippo – Trusted insurance comparison service"
        width={213}
        height={40}
        sx={{ width: '213px', height: '40px' }}
      />

      {/* Text */}
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          fontSize: '0.875rem',
          lineHeight: '1rem',
          color: '#738289',
          whiteSpace: 'pre-line',
        }}
      >
        Hippo Comparative Services (Pty) Ltd is an authorised financial service
        provider.{'\n'}
        {'\n'}FSP number: 16357. Block A, Steyn City Capital Park, Erling Road,
        Riverglen, Dainfern, 2191{'\n'}
        {'\n'}
        {disclaimer}
      </Typography>

      {/* Social Icons */}
      <Box
        sx={{ display: 'flex', gap: '25px', justifyContent: 'center', pt: 1 }}
      >
        {socialLinks.map(({ icon, url, alt, title }, i) => (
          <Box
            key={i}
            {...linkProps(url)}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Image
              src={icon}
              alt={alt}
              title={title}
              width={36}
              height={36}
              sx={{ width: '100%', height: '100%' }}
            />
          </Box>
        ))}
      </Box>

      {/* Collapsible Nav Sections */}
      {navGroups.map((group, i) => (
        <Box
          key={group.title}
          sx={{
            borderBottom: '1px solid #E0E0E0',
            mx: '-16px',
            px: '16px',
            pb: 1,
          }}
        >
          <Box
            onClick={() => toggle(i)}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 1,
              cursor: 'pointer',
            }}
          >
            <Typography
              component={CATEGORY_LANDING_HREFS[group.title] ? 'a' : 'span'}
              href={CATEGORY_LANDING_HREFS[group.title] || undefined}
              onClick={(e) => {
                if (CATEGORY_LANDING_HREFS[group.title]) e.stopPropagation();
              }}
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '1rem',
                lineHeight: '1.5rem',
                letterSpacing: '-0.002em',
                color: '#162F3B',
                textDecoration: 'none',
                '&:hover': CATEGORY_LANDING_HREFS[group.title]
                  ? { color: '#C7CA00', textDecoration: 'underline' }
                  : undefined,
              }}
            >
              {group.title}
            </Typography>
            <IconButton
              disableRipple
              tabIndex={-1}
              aria-hidden="true"
              sx={{ width: '20px', height: '20px', p: 0 }}
            >
              {openIndex === i ? (
                <ArrowDropUpIcon sx={{ fontSize: '20px', color: '#162F3B' }} />
              ) : (
                <ArrowDropDownIcon
                  sx={{ fontSize: '20px', color: '#162F3B' }}
                />
              )}
            </IconButton>
          </Box>

          {openIndex === i && (
            <Box
              sx={{
                pl: 2,
                pt: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              {group.links.map((link, j) => (
                <Typography
                  key={j}
                  {...linkProps(link.href, {
                    target: link.target,
                    rel: link.rel,
                  })}
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: '0.875rem',
                    lineHeight: '1.25rem',
                    letterSpacing: '-0.002em',
                    color: '#738289',
                    textDecoration: 'none',
                    py: '12px',
                    '&:hover': {
                      color: '#C7CA00',
                      fontWeight: 700,
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {link.label}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      ))}

      {/* Copyright */}
      <Typography
        sx={{
          fontFamily: 'Overline, sans-serif',
          fontWeight: 600,
          fontSize: '0.75rem',
          lineHeight: '1.25rem',
          color: '#738289',
          textAlign: 'center',
          letterSpacing: '-0.01em',
          width: '311px',
          mx: 'auto',
        }}
      >
        © {new Date().getFullYear()} Hippo Comparative Services (Pty) Ltd. All
        Rights Reserved.
      </Typography>
    </Box>
  );
};

export default FooterMobileComponent;
